import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { FormModal, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import RecordActionsCard from '../components/workflow/RecordActionsCard';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import { useRemovableFiles } from '../components/workflow/EditableAttachments';
import { ScreeningAnswersView } from '../components/shared/ScreeningFields';
import {
  screeningRecordToForm, screeningRecordGaps, screeningRecordFormData,
  useScreeningRecordOptions, ScreeningRecordFormFields, ScreeningRecordSourceBadge,
} from '../components/records/screeningRecordKind';
import { formatDate } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import NowName from '../components/shared/NowName';
import { ScreeningCompaniesList } from '../components/workflow/ScreeningCompanies';
import { ProfileDetailItem } from '../components/workflow/ProfileDetail';
import { pieText } from '../utils/engagementProfile';
import HistoryCard from '../components/workflow/HistoryCard';
import { SCREENING_CHANGE_LABELS } from './WorkflowScreeningDetail';

// Management — one Screening record (docs/modules/workflow-screening.md
// §記錄層). Read view + Edit modal + Delete; the record page is where a wrong
// answer or a wrong PDF is corrected after the request is locked.

// History reads like the Screening request's: the same HistoryCard and field
// labels, plus what only the record carries.
const RECORD_CHANGE_LABELS = { ...SCREENING_CHANGE_LABELS, completedOn: 'Completion Date' };
const RECORD_ACTION_LABELS = {
  created: ['Created', 'bg-green-50 text-green-700'],
  updated: ['Updated', 'bg-blue-50 text-blue-700'],
  file_replaced: ['Screening document replaced', 'bg-blue-50 text-blue-700'],
};

const QMScreeningRecordDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON(`/api/qm/screening-records/${id}`);
      setRecord(data.record);
      setEvents(data.events || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const currentFiles = (record?.files || []).filter((f) => !f.supersededAt);
  const previousFiles = (record?.files || []).filter((f) => f.supersededAt);
  const service = record && (record.serviceProvided === 'Other'
    ? `Other — ${record.serviceOther || ''}` : record?.serviceProvided);

  return (
    <RequestDetailShell
      backTo="/qm/screening-records"
      backLabel="Screening Records"
      loading={!record}
      error={error}
      notice={notice}
    >
      {record && (
        <>
          <RequestHeader
            requestNo={`Screening record #${record.id}`}
            typeLabel="Screening Record"
            badge={<ScreeningRecordSourceBadge record={record} />}
            originLabel={record.source === 'workflow:screening' ? `produced by Screening request ${record.sourceRequestNo || ''}`.trim() : 'entered in Management'}
            submittedAt={record.createdAt}
          />

          <RecordActionsCard onEdit={() => setEditing(true)} onDelete={() => setDeleting(true)}>
            {record.sourceRequestId && (
              <RefLink to={`/workflow/screening/${record.sourceRequestId}`} className="ml-auto text-sm text-blue-600 hover:text-blue-800 underline">
                Open Request {record.sourceRequestNo}
              </RefLink>
            )}
          </RecordActionsCard>

          <SectionCard title="Companies">
            <DetailGrid>
              <DetailItem label="Client Group"><NowName value={record.clientGroup} now={record.clientGroupNow} /></DetailItem>
              <DetailItem label="Completion Date">{formatDate(record.completedOn)}</DetailItem>
              <DetailItem label="Remarks">{record.remarks || '—'}</DetailItem>
              <div className="sm:col-span-2">
                <DetailItem label={`Companies (${(record.entities || []).length})`}>
                  <ScreeningCompaniesList entities={record.entities || []} />
                </DetailItem>
              </div>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Client Profile">
            <DetailGrid>
              {/* Each answer reads per company when the companies answered it on their own. */}
              <ProfileDetailItem request={record} groupKey="service" label="Service Provided">{service || '—'}</ProfileDetailItem>
              {/* The description reads under the service it describes. */}
              <ProfileDetailItem request={record} groupKey="descriptionOfService" label="Description of Service" />
              <ProfileDetailItem request={record} groupKey="pie" label="Public Interest Entity (PIE)">{pieText(record) || '—'}</ProfileDetailItem>
              <DetailItem label="Estimated Fees">{record.proposedFees != null ? `${record.proposedFeesCcy || 'SGD'} ${record.proposedFees.toLocaleString()}` : '—'}</DetailItem>
              <DetailItem label="Ultimate Parent">{record.ultimateParent || '—'}</DetailItem>
              <ProfileDetailItem request={record} groupKey="overseasOperations" label="Overseas Operations / Overseas Subsidiary" />
              <ProfileDetailItem request={record} groupKey="consolidatedFs" label="Consolidated Financial Statements" />
              <ProfileDetailItem request={record} groupKey="ssicPrimary" label="SSIC Code Primary" />
              <ProfileDetailItem request={record} groupKey="ssicSecondary" label="SSIC Code Secondary" />
              <ProfileDetailItem request={record} groupKey="principalActivity1" label="Principal Activity 1" />
              <ProfileDetailItem request={record} groupKey="principalActivity2" label="Principal Activity 2" />
              <ProfileDetailItem request={record} groupKey="secRegistered" label="Registered with the SEC (US)" />
              <ProfileDetailItem request={record} groupKey="isGroupAudit" label="Group Audit" />
              <ProfileDetailItem request={record} groupKey="underInvestigation" label="Under Investigation by Regulatory Authorities" />
              <ProfileDetailItem request={record} groupKey="tcwgFeeCommunicated" label="Fee Information Communicated to TCWG" />
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Screening">
            <ScreeningAnswersView r={record} anchor={() => null} />
          </SectionCard>

          <SectionCard title="Documents">
            <AttachmentsList
              files={currentFiles}
              downloadHref={(fl) => `${API_BASE_URL}/api/qm/screening-records/${record.id}/files/${fl.id}`}
              emptyText="No documents on this record — edit it to attach the screening PDF."
            />
            {previousFiles.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-slate-500 mb-1">Previous Versions</p>
                <AttachmentsList
                  files={previousFiles}
                  downloadHref={(fl) => `${API_BASE_URL}/api/qm/screening-records/${record.id}/files/${fl.id}`}
                />
              </div>
            )}
          </SectionCard>

          <HistoryCard events={events || []} actionLabels={RECORD_ACTION_LABELS} changeLabels={RECORD_CHANGE_LABELS} />

          {editing && (
            <ScreeningRecordEditModal
              record={record}
              onClose={() => setEditing(false)}
              onDone={() => { setEditing(false); setNotice('Record updated.'); load(); }}
            />
          )}
          {deleting && (
            <ConfirmDeleteModal
              title={`Delete Screening Record #${record.id}`}
              message="This permanently deletes the record. Linked engagements stop showing its screening document. This cannot be undone."
              onClose={() => setDeleting(false)}
              onConfirm={async () => { await fetchJSON(`/api/qm/screening-records/${record.id}`, { method: 'DELETE' }); navigate('/qm/screening-records'); }}
            />
          )}
        </>
      )}
    </RequestDetailShell>
  );
};

const ScreeningRecordEditModal = ({ record, onClose, onDone }) => {
  const { options, optionsError } = useScreeningRecordOptions();
  const [f, setF] = useState(() => screeningRecordToForm(record));
  const [screeningFile, setScreeningFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const { removeIds, toggle: toggleRemove } = useRemovableFiles();
  const gaps = useMemo(() => screeningRecordGaps(f), [f]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      await fetchJSON(`/api/qm/screening-records/${record.id}`, {
        method: 'PATCH', body: screeningRecordFormData(f, { screeningFile, files, removeFileIds: removeIds }),
      });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Edit Screening Record #${record.id}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ScreeningRecordFormFields
          f={f} set={set} options={options} current={record}
          screeningFile={screeningFile} setScreeningFile={setScreeningFile}
          files={files} setFiles={setFiles}
          removeIds={removeIds} onToggleRemove={toggleRemove}
        />
      </FormValidationProvider>
      {(error || validation.error || optionsError) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <span>{error || validation.error || optionsError}</span>
        </div>
      )}
    </FormModal>
  );
};

export default QMScreeningRecordDetail;
